/**
 * Validation Utilities
 * Module 07 - Dashboard UI
 * 
 * Common validation functions for forms and data
 */

/**
 * Validate email address format
 * 
 * @param email - Email string to validate
 * @returns True if valid email format
 * 
 * @example
 * isValidEmail('user@example.com') // true
 * isValidEmail('invalid-email') // false
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 * Requirements: min 8 characters, 1 uppercase, 1 lowercase, 1 number
 * 
 * @param password - Password string to validate
 * @returns True if password meets requirements
 * 
 * @example
 * isStrongPassword('Pass1234') // true
 * isStrongPassword('weak') // false
 */
export function isStrongPassword(password: string): boolean {
  if (password.length < 8) return false;
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  
  return hasUpperCase && hasLowerCase && hasNumber;
}

/**
 * Validate domain format
 * 
 * @param domain - Domain string to validate
 * @returns True if valid domain format
 * 
 * @example
 * isValidDomain('example.com') // true
 * isValidDomain('sub.example.co.uk') // true
 * isValidDomain('*.example.com') // true (wildcard)
 * isValidDomain('invalid') // false
 */
export function isValidDomain(domain: string): boolean {
  if (!domain || domain.trim() === '') return false;
  
  // Allow wildcard subdomains
  const domainRegex = /^(\*\.)?([a-z0-9]+([.-]{1}[a-z0-9]+)*\.[a-z]{2,}|localhost)(:\d+)?$/i;
  return domainRegex.test(domain.trim());
}

/**
 * Validate file extension
 * 
 * @param filename - Filename to validate
 * @param allowedExtensions - Array of allowed extensions (without dot)
 * @returns True if file has allowed extension
 * 
 * @example
 * isValidFileExtension('model.glb', ['glb', 'gltf']) // true
 * isValidFileExtension('image.png', ['glb', 'gltf']) // false
 */
export function isValidFileExtension(filename: string, allowedExtensions: string[]): boolean {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension ? allowedExtensions.includes(extension) : false;
}

/**
 * Validate file size
 * 
 * @param fileSize - File size in bytes
 * @param maxSizeBytes - Maximum allowed size in bytes
 * @returns True if file size is within limit
 * 
 * @example
 * isValidFileSize(1024 * 1024, 50 * 1024 * 1024) // true (1MB < 50MB)
 * isValidFileSize(100 * 1024 * 1024, 50 * 1024 * 1024) // false (100MB > 50MB)
 */
export function isValidFileSize(fileSize: number, maxSizeBytes: number): boolean {
  return fileSize > 0 && fileSize <= maxSizeBytes;
}

/**
 * Validate URL format
 * 
 * @param url - URL string to validate
 * @returns True if valid URL format
 * 
 * @example
 * isValidUrl('https://example.com') // true
 * isValidUrl('invalid-url') // false
 */
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitise filename by removing invalid characters
 * 
 * @param filename - Filename to sanitise
 * @returns Sanitised filename
 * 
 * @example
 * sanitizeFilename('my model (v2).glb') // "my-model-v2.glb"
 * sanitizeFilename('file/with\\slashes.glb') // "file-with-slashes.glb"
 */
export function sanitizeFilename(filename: string): string {
  // Remove invalid filename characters and replace with dash
  return filename
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();
}

/**
 * Validate embed name
 * 
 * @param name - Embed name to validate
 * @returns True if valid name (3-100 characters)
 * 
 * @example
 * isValidEmbedName('Product Viewer') // true
 * isValidEmbedName('A') // false (too short)
 */
export function isValidEmbedName(name: string): boolean {
  return name.trim().length >= 3 && name.trim().length <= 100;
}

/**
 * Get password strength level
 * 
 * @param password - Password to evaluate
 * @returns Strength level: 'weak', 'medium', or 'strong'
 * 
 * @example
 * getPasswordStrength('pass') // 'weak'
 * getPasswordStrength('Password1') // 'medium'
 * getPasswordStrength('Pass1234!@') // 'strong'
 */
export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  if (password.length < 6) return 'weak';
  if (!isStrongPassword(password)) return 'weak';
  
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 12;
  
  if (hasSpecialChar && isLongEnough) return 'strong';
  return 'medium';
}
